import { InvoiceStatus, TBusiness, Client } from '../types'
import { computeInvoiceTotals, isInvoiceTotalZero } from '../utils'
import {
  DraftClient,
  GuestInvoiceDraftPayload,
  GuestPendingIntent,
} from './guestInvoiceDraftStorage'

export type ProfileOverwriteChoice = 'accept' | 'decline'

export type SavedUserProfile = {
  businessDetails?: (TBusiness & { logoSrc?: string }) | null
}

export type PlanLimitContext = {
  invoiceCount: number
  maxAmountOfInvoices: number
  hasActiveSubscription: boolean
  freeAccess: boolean
}

export type GuestHydrationInput = {
  draft: GuestInvoiceDraftPayload | null
  profile: SavedUserProfile
  clients: Client[]
  planLimits: PlanLimitContext
  overwriteChoice?: ProfileOverwriteChoice
  accountEmail?: string
}

export type ClientResolution =
  | { action: 'link'; clientId: string }
  | {
      action: 'create'
      payload: {
        'client.tradingName': string
        'client.email': string
        'client.billingAddress': string
        'client.name'?: string
        'client.lastName'?: string
      }
    }

export type ProfileUpdatePlan = {
  shouldUpdate: boolean
  businessDetailsPatch?: Record<string, string | number | boolean>
  logoUpload?: string
}

export type PostSaveAction =
  | { type: 'navigate'; path: string }
  | { type: 'openPdf'; invoiceId: string }

export type GuestHydrationExecutionPlan = {
  profileUpdate: ProfileUpdatePlan
  clientResolution: ClientResolution
  invoiceSaveBody: Record<string, unknown>
  guestCode: string
  postSaveAction: PostSaveAction
  pendingIntent: GuestPendingIntent
}

export type AuthenticatedInvoiceDraftHandoff = {
  values: GuestInvoiceDraftPayload['values']
  paymentInstructionsDraft: string
  draftClient: DraftClient
}

export type GuestHydrationFailureFallback = {
  preserveGuestDraft: true
  redirectTo: '/invoices/new'
  guestDraftToKeep: GuestInvoiceDraftPayload
  authenticatedDraft: AuthenticatedInvoiceDraftHandoff
}

export type GuestHydrationOutcome =
  | ({ status: 'invalid' } & GuestHydrationFailureFallback)
  | { status: 'invalid'; preserveGuestDraft: true; redirectTo: '/invoices/new' }
  | { status: 'planLimitBlocked'; preserveGuestDraft: true; redirectTo: '/get-premium' }
  | { status: 'awaitProfileChoice' }
  | { status: 'execute'; plan: GuestHydrationExecutionPlan }

const normalize = (value: unknown): string => String(value ?? '').trim()

const normalizeEmail = (value: unknown): string => normalize(value).toLowerCase()

export const hasSavedBusinessDetails = (profile: SavedUserProfile): boolean =>
  normalize(profile.businessDetails?.name) !== ''

const taxMatches = (
  profileTax: TBusiness['tax'] | undefined,
  draftTax: TBusiness['tax'] | undefined,
  profileEnableTax: boolean | undefined,
  draftEnableTax: boolean | undefined,
): boolean =>
  !!profileEnableTax === !!draftEnableTax
  && normalize(profileTax?.name) === normalize(draftTax?.name)
  && normalize(profileTax?.percentage) === normalize(draftTax?.percentage)

export const businessDetailsMatchProfile = (
  profile: SavedUserProfile,
  draft: GuestInvoiceDraftPayload,
): boolean => {
  const saved = profile.businessDetails
  const draftBusiness = draft.values.businessDetails
  if (!saved) return false

  const logoMatches = normalize(draft.logoSrc) === ''
    || normalize(saved.logoSrc) === normalize(draft.logoSrc)

  return (
    normalize(saved.name) === normalize(draftBusiness.name)
    && normalize(saved.billingAddress) === normalize(draftBusiness.billingAddress)
    && normalize(saved.currency?.code) === normalize(draftBusiness.currency?.code)
    && normalize(saved.taxRegistrationNumber) === normalize(draftBusiness.taxRegistrationNumber)
    && taxMatches(saved.tax, draftBusiness.tax, saved.enableTax, draftBusiness.enableTax)
    && logoMatches
  )
}

export const shouldShowProfileOverwritePrompt = (
  profile: SavedUserProfile,
  draft: GuestInvoiceDraftPayload,
): boolean =>
  hasSavedBusinessDetails(profile) && !businessDetailsMatchProfile(profile, draft)

export const isAtInvoicePlanLimit = (planLimits: PlanLimitContext): boolean =>
  planLimits.invoiceCount >= planLimits.maxAmountOfInvoices
  && !planLimits.hasActiveSubscription
  && !planLimits.freeAccess

export const resolveGuestClient = (
  draftClient: DraftClient,
  clients: Client[],
): ClientResolution => {
  const normalizedEmail = normalizeEmail(draftClient.email)
  if (normalizedEmail) {
    const existing = clients.find(
      (client) => normalizeEmail(client.email) === normalizedEmail,
    )
    if (existing?._id) {
      return { action: 'link', clientId: String(existing._id) }
    }
  }

  return {
    action: 'create',
    payload: {
      'client.tradingName': draftClient.tradingName,
      'client.email': draftClient.email,
      'client.billingAddress': draftClient.billingAddress,
      ...(draftClient.name ? { 'client.name': draftClient.name } : {}),
      ...(draftClient.lastName ? { 'client.lastName': draftClient.lastName } : {}),
    },
  }
}

const shouldApplyDraftToProfile = (
  profile: SavedUserProfile,
  overwriteChoice?: ProfileOverwriteChoice,
): boolean => {
  if (!hasSavedBusinessDetails(profile)) return true
  if (overwriteChoice === 'accept') return true
  return false
}

const resolveProfileEmailPatch = (
  profile: SavedUserProfile,
  draftEmail: string,
  accountEmail?: string,
): string | undefined => {
  const normalizedDraftEmail = normalizeEmail(draftEmail)
  if (normalizedDraftEmail) return normalizedDraftEmail
  if (!hasSavedBusinessDetails(profile) && accountEmail) {
    return normalizeEmail(accountEmail)
  }
  return undefined
}

export const buildProfileUpdatePlan = (
  profile: SavedUserProfile,
  draft: GuestInvoiceDraftPayload,
  overwriteChoice?: ProfileOverwriteChoice,
  accountEmail?: string,
): ProfileUpdatePlan => {
  const draftBusiness = draft.values.businessDetails
  const shouldUpdate = shouldApplyDraftToProfile(profile, overwriteChoice)
  if (!shouldUpdate) {
    if (overwriteChoice !== 'decline') {
      const savedPaymentInstructions = normalize(profile.businessDetails?.paymentInstructions)
      const draftPaymentInstructions = normalize(draft.paymentInstructionsDraft)
      if (!savedPaymentInstructions && draftPaymentInstructions) {
        return {
          shouldUpdate: true,
          businessDetailsPatch: {
            'businessDetails.paymentInstructions': draftPaymentInstructions,
          },
        }
      }
    }
    return { shouldUpdate: false }
  }

  const businessDetailsPatch: Record<string, string | number | boolean> = {
    'businessDetails.name': draftBusiness.name,
    'businessDetails.billingAddress': normalize(draftBusiness.billingAddress),
    'businessDetails.taxRegistrationNumber': normalize(draftBusiness.taxRegistrationNumber),
    'businessDetails.currency.code': draftBusiness.currency.code,
    'businessDetails.enableTax': !!draftBusiness.enableTax,
    'businessDetails.tax.name': normalize(draftBusiness.tax?.name),
    'businessDetails.tax.percentage': draftBusiness.tax?.percentage ?? '',
  }

  const profileEmail = resolveProfileEmailPatch(profile, draftBusiness.email, accountEmail)
  if (profileEmail) {
    businessDetailsPatch['businessDetails.email'] = profileEmail
  }

  const draftPaymentInstructions = normalize(draft.paymentInstructionsDraft)
  if (draftPaymentInstructions) {
    businessDetailsPatch['businessDetails.paymentInstructions'] = draftPaymentInstructions
  }

  return {
    shouldUpdate: true,
    businessDetailsPatch,
    logoUpload: draft.logoSrc,
  }
}

export const buildInvoiceSaveBody = (
  draft: GuestInvoiceDraftPayload,
  clientId: string,
): Record<string, unknown> => {
  const enableTax = !!draft.values.businessDetails?.enableTax
  const { total, totalTax } = computeInvoiceTotals(draft.values.items ?? [], enableTax)
  return {
    clientId,
    items: draft.values.items,
    total,
    totalTax,
    issueDate: draft.values.issueDate,
    dueDate: draft.values.dueDate,
    code: draft.values.code,
    status: draft.values.status ?? InvoiceStatus.DRAFT,
    locked: false,
  }
}

export const buildPostSaveAction = (
  pendingIntent: GuestPendingIntent,
  invoiceId: string,
): PostSaveAction =>
  pendingIntent === 'send'
    ? { type: 'navigate', path: `/invoices/${invoiceId}/send-email` }
    : { type: 'openPdf', invoiceId }

export type PostSaveRedirect =
  | { kind: 'spa'; path: string }
  | { kind: 'external'; url: string; companionSpaPath: string }

export const buildInvoicePdfUrl = (apiBaseUrl: string, invoiceId: string): string =>
  `${apiBaseUrl.replace(/\/$/, '')}/api/invoices/pdf/${invoiceId}`

export const buildInvoiceEditPath = (invoiceId: string): string => `/invoices/${invoiceId}`

export const resolvePostSaveRedirect = (
  action: PostSaveAction,
  apiBaseUrl: string,
): PostSaveRedirect =>
  action.type === 'navigate'
    ? { kind: 'spa', path: action.path }
    : {
        kind: 'external',
        url: buildInvoicePdfUrl(apiBaseUrl, action.invoiceId),
        companionSpaPath: buildInvoiceEditPath(action.invoiceId),
      }

export const applyExternalPostSaveRedirect = (
  redirect: Extract<PostSaveRedirect, { kind: 'external' }>,
  assignThisTab: (path: string) => void,
  openPdfTab: (url: string) => void,
): void => {
  openPdfTab(redirect.url)
  assignThisTab(redirect.companionSpaPath)
}

export const shouldConfirmZeroTotalSend = (draft: GuestInvoiceDraftPayload): boolean => {
  if (draft.pendingIntent !== 'send') return false
  const enableTax = !!draft.values.businessDetails?.enableTax
  return isInvoiceTotalZero(draft.values.items ?? [], enableTax)
}

export const applyServerCodeToSaveBody = (
  body: Record<string, unknown>,
  serverCode: string,
): Record<string, unknown> => ({
  ...body,
  code: serverCode,
})

export const isInvoiceCodeConflictError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? '')
  return message.includes('invoice code already exists')
}

export const buildHydrationFailureFallback = (
  draft: GuestInvoiceDraftPayload,
): GuestHydrationFailureFallback => ({
  preserveGuestDraft: true,
  redirectTo: '/invoices/new',
  guestDraftToKeep: {
    ...draft,
    pendingIntent: undefined,
  },
  authenticatedDraft: {
    values: {
      ...draft.values,
      _id: undefined,
      clientId: '',
      locked: false,
      businessDetails: {
        ...draft.values.businessDetails,
        ...(draft.logoSrc ? { logoSrc: draft.logoSrc } : {}),
      },
    },
    paymentInstructionsDraft: draft.paymentInstructionsDraft ?? '',
    draftClient: draft.draftClient,
  },
})

const invalidHydrationOutcome = (
  draft: GuestInvoiceDraftPayload,
): Extract<GuestHydrationOutcome, { status: 'invalid' }> => ({
  status: 'invalid',
  ...buildHydrationFailureFallback(draft),
})

export const planGuestHydration = (input: GuestHydrationInput): GuestHydrationOutcome => {
  const { draft, profile, clients, planLimits, overwriteChoice, accountEmail } = input

  if (!draft) {
    return { status: 'invalid', preserveGuestDraft: true, redirectTo: '/invoices/new' }
  }

  if (!draft.pendingIntent) {
    return invalidHydrationOutcome(draft)
  }

  if (!normalize(draft.draftClient.tradingName)) {
    return invalidHydrationOutcome(draft)
  }

  if (isAtInvoicePlanLimit(planLimits)) {
    return { status: 'planLimitBlocked', preserveGuestDraft: true, redirectTo: '/get-premium' }
  }

  if (shouldShowProfileOverwritePrompt(profile, draft) && !overwriteChoice) {
    return { status: 'awaitProfileChoice' }
  }

  const clientResolution = resolveGuestClient(draft.draftClient, clients)
  const resolvedClientId =
    clientResolution.action === 'link' ? clientResolution.clientId : 'pending-client-id'

  return {
    status: 'execute',
    plan: {
      profileUpdate: buildProfileUpdatePlan(profile, draft, overwriteChoice, accountEmail),
      clientResolution,
      invoiceSaveBody: buildInvoiceSaveBody(draft, resolvedClientId),
      guestCode: normalize(draft.values.code),
      postSaveAction: buildPostSaveAction(draft.pendingIntent, 'pending-invoice-id'),
      pendingIntent: draft.pendingIntent,
    },
  }
}

export const withResolvedInvoiceId = (
  plan: GuestHydrationExecutionPlan,
  invoiceId: string,
): GuestHydrationExecutionPlan => ({
  ...plan,
  postSaveAction: buildPostSaveAction(plan.pendingIntent, invoiceId),
})

export const withResolvedClientId = (
  body: Record<string, unknown>,
  clientId: string,
): Record<string, unknown> => ({
  ...body,
  clientId,
})
